import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { format, addDays } from 'date-fns';
import type { AdType } from '../types/ads.types';
import { devtoolsConfig } from '@/lib/stores/secure-storage';

// ============ Types ============

export interface SelectedProduct {
  refId: string;
  name: string;
  image?: string;
  price?: number;
}

export interface SelectedShop {
  id: string;
  name: string;
  logo?: string;
  productCount?: number;
  rating?: number;
}

export interface BannerAction {
  type: 'product' | 'shop';
  productRefId?: string;
}

export interface WizardData {
  type: AdType | null;
  products: SelectedProduct[];
  selectedShop: SelectedShop | null;
  name: string;
  title: string; // Notification title (max 50 chars)
  message: string;
  budget: number;
  dailyBudget: number | null;
  bidAmount: number | null;
  startDate: string;
  endDate: string;
  // New fields for banner/carousel
  bannerImage: File | null;
  bannerImagePreview: string | null;
  bannerAction: BannerAction;
  headline: string; // Promotional text for carousel/banner (max 40 chars)
}

export type ObjectiveFilter = 'visibility' | 'notification' | null;

interface CampaignWizardState {
  // Current step (0-indexed)
  currentStep: number;

  // Wizard data
  data: WizardData;

  // Filter for objective-based navigation
  filterObjective: ObjectiveFilter;

  // Actions - Navigation
  setCurrentStep: (step: number) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;

  // Actions - Data
  setType: (type: AdType) => void;
  setProducts: (products: SelectedProduct[]) => void;
  setSelectedShop: (shop: SelectedShop | null) => void;
  setName: (name: string) => void;
  setMessage: (message: string) => void;
  setTitle: (title: string) => void;
  setBudget: (budget: number) => void;
  setDailyBudget: (budget: number | null) => void;
  setBidAmount: (amount: number | null) => void;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  setBannerImage: (file: File | null, preview: string | null) => void;
  setBannerAction: (action: BannerAction) => void;
  setHeadline: (headline: string) => void;

  // Actions - Batch updates
  updateData: (partialData: Partial<WizardData>) => void;

  // Actions - URL params initialization
  initFromParams: (params: { type?: AdType | null; objective?: ObjectiveFilter }) => void;

  // Actions - Reset
  resetWizard: () => void;
}

// ============ Initial State ============

const getInitialData = (): WizardData => ({
  type: null,
  products: [],
  selectedShop: null,
  name: '',
  title: '',
  message: '',
  budget: 20,
  dailyBudget: null,
  bidAmount: null,
  startDate: `${format(new Date(), 'yyyy-MM-dd')}T00:00:00.000Z`,
  endDate: `${format(addDays(new Date(), 29), 'yyyy-MM-dd')}T23:59:00.000Z`,
  bannerImage: null,
  bannerImagePreview: null,
  bannerAction: { type: 'shop' },
  headline: '',
});

// ============ Store ============

export const useCampaignWizardStore = create<CampaignWizardState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        currentStep: 0,
        data: getInitialData(),
        filterObjective: null,

        // Navigation
        setCurrentStep: (step) => set({ currentStep: step }),

        goToNextStep: () => {
          const { currentStep } = get();
          if (currentStep < 3) {
            set({ currentStep: currentStep + 1 });
          }
        },

        goToPreviousStep: () => {
          const { currentStep } = get();
          if (currentStep > 0) {
            set({ currentStep: currentStep - 1 });
          }
        },

        // Data setters
        setType: (type) =>
          set((state) => ({
            data: { ...state.data, type },
          })),

        setProducts: (products) =>
          set((state) => ({
            data: { ...state.data, products },
          })),

        setSelectedShop: (selectedShop) =>
          set((state) => ({
            data: { ...state.data, selectedShop },
          })),

        setName: (name) =>
          set((state) => ({
            data: { ...state.data, name },
          })),

        setMessage: (message) =>
          set((state) => ({
            data: { ...state.data, message },
          })),

        setTitle: (title) =>
          set((state) => ({
            data: { ...state.data, title },
          })),

        setBudget: (budget) =>
          set((state) => ({
            data: { ...state.data, budget },
          })),

        setDailyBudget: (dailyBudget) =>
          set((state) => ({
            data: { ...state.data, dailyBudget },
          })),

        setBidAmount: (bidAmount) =>
          set((state) => ({
            data: { ...state.data, bidAmount },
          })),

        setStartDate: (startDate) =>
          set((state) => ({
            data: { ...state.data, startDate },
          })),

        setEndDate: (endDate) =>
          set((state) => ({
            data: { ...state.data, endDate },
          })),

        setBannerImage: (bannerImage, bannerImagePreview) =>
          set((state) => ({
            data: { ...state.data, bannerImage, bannerImagePreview },
          })),

        setBannerAction: (bannerAction) =>
          set((state) => ({
            data: { ...state.data, bannerAction },
          })),

        setHeadline: (headline) =>
          set((state) => ({
            data: { ...state.data, headline },
          })),

        // Batch update
        updateData: (partialData) =>
          set((state) => ({
            data: { ...state.data, ...partialData },
          })),

        // Initialize from URL params
        initFromParams: (params) => {
          const { type, objective } = params;

          if (type) {
            // Direct type selection - skip step 0 and go to step 1
            set((state) => ({
              currentStep: 1,
              filterObjective: null,
              data: { ...state.data, type },
            }));
          } else if (objective) {
            // Objective-based - stay on step 0 but filter options
            set({
              currentStep: 0,
              filterObjective: objective,
              data: getInitialData(),
            });
          } else {
            // No params - reset to default
            set({
              currentStep: 0,
              filterObjective: null,
              data: getInitialData(),
            });
          }
        },

        // Reset
        resetWizard: () =>
          set({
            currentStep: 0,
            filterObjective: null,
            data: getInitialData(),
          }),
      }),
      {
        name: 'campaign-wizard-storage',
        storage: createJSONStorage(() => sessionStorage),
        // Don't persist File objects - they can't be serialized
        partialize: (state) => ({
          currentStep: state.currentStep,
          filterObjective: state.filterObjective,
          data: {
            ...state.data,
            bannerImage: null, // Don't persist File
          },
        }),
      }
    ),
    { ...devtoolsConfig, name: 'campaign-wizard-store' }
  )
);

// ============ Selectors ============

export const selectCurrentStep = (state: CampaignWizardState) => state.currentStep;
export const selectWizardData = (state: CampaignWizardState) => state.data;
export const selectCampaignType = (state: CampaignWizardState) => state.data.type;
export const selectProducts = (state: CampaignWizardState) => state.data.products;
export const selectSelectedShop = (state: CampaignWizardState) => state.data.selectedShop;
export const selectBannerImage = (state: CampaignWizardState) => state.data.bannerImage;
export const selectBannerImagePreview = (state: CampaignWizardState) => state.data.bannerImagePreview;
export const selectBannerAction = (state: CampaignWizardState) => state.data.bannerAction;
export const selectHeadline = (state: CampaignWizardState) => state.data.headline;
export const selectTitle = (state: CampaignWizardState) => state.data.title;
export const selectFilterObjective = (state: CampaignWizardState) => state.filterObjective;
